import React from 'react';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
  Tooltip
} from 'recharts';

// RMMBR brand colors - using CSS variables when possible
const CHART_COLORS = {
  primary: 'var(--accent)',
  secondary: '#a87f52',
  tertiary: '#8d6e47',
  comparison: '#9b7c54'
};

function WheelRadarChart({ scores, comparisonScores = null, showComparison = false }) {
  // Transform scores into recharts format
  const chartData = scores.map(score => ({
    category: score.category.replace(' & ', '\n'),
    current: score.score,
    ...(showComparison && comparisonScores ? {
      previous: comparisonScores.find(s => s.category === score.category)?.score || 0
    } : {})
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-[var(--accent)]/30 rounded-lg p-3 shadow-lg">
          <p className="text-[#3a352f] font-semibold mb-1">{payload[0].payload.category.replace('\n', ' & ')}</p>
          <p className="text-[var(--accent)] text-sm">
            Current: <span className="font-bold">{payload[0].value}/10</span>
          </p>
          {showComparison && payload[1] && (
            <p className="text-[#9b7c54] text-sm">
              Previous: <span className="font-bold">{payload[1].value}/10</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
      <ResponsiveContainer width="100%" height={500}>
        <RadarChart data={chartData}>
          <PolarGrid stroke="#C59D52" strokeOpacity={0.2} />
          <PolarAngleAxis
            dataKey="category"
            tick={{ fill: '#e0e0e0', fontSize: 12 }}
            tickLine={false}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 10]}
            tick={{ fill: '#a0a0a0', fontSize: 10 }}
            tickCount={6}
          />

          {/* Current scores */}
          <Radar
            name="Current"
            dataKey="current"
            stroke={CHART_COLORS.primary}
            fill={CHART_COLORS.primary}
            fillOpacity={0.5}
            strokeWidth={2}
          />

          {/* Comparison scores (if enabled) */}
          {showComparison && comparisonScores && (
            <Radar
              name="Previous"
              dataKey="previous"
              stroke={CHART_COLORS.comparison}
              fill={CHART_COLORS.comparison}
              fillOpacity={0.3}
              strokeWidth={2}
              strokeDasharray="5 5"
            />
          )}

          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '14px',
              color: '#e0e0e0'
            }}
            iconType="line"
          />
        </RadarChart>
      </ResponsiveContainer>

      {/* Score summary below chart */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {scores.map((score) => (
          <div key={score.category} className="bg-white/5 rounded-lg p-3">
            <p className="text-xs text-gray-400 mb-1">{score.category}</p>
            <div className="flex items-baseline gap-2">
              <p className="text-2xl font-bold text-[var(--accent)]">{score.score}</p>
              <p className="text-xs text-gray-500">/10</p>
              {showComparison && comparisonScores && (() => {
                const prevScore = comparisonScores.find(s => s.category === score.category)?.score || 0;
                const diff = score.score - prevScore;
                if (diff === 0) return null;
                return (
                  <span className={`text-xs font-semibold ${diff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {diff > 0 ? '+' : ''}{diff}
                  </span>
                );
              })()}
            </div>
          </div>
        ))}
      </div>

      {/* Overall balance score */}
      <div className="mt-6 bg-gradient-to-br from-[#f5e6d3] to-[#ede0cf] rounded-xl p-4 border-l-4 border-[var(--accent)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-[#5a5248] font-medium">Overall Balance</p>
            <p className="text-xs text-gray-600 mt-1">Average satisfaction across all life areas</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-[var(--accent)]">
              {(scores.reduce((sum, s) => sum + s.score, 0) / scores.length).toFixed(1)}
            </p>
            <p className="text-xs text-gray-600">/ 10</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WheelRadarChart;
